import type { Point } from "../scene/model";
import type { PathAnchor } from "./vector-path";

const distance = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);
const clamp = (v: number, low: number, high: number) =>
  Math.max(low, Math.min(high, v));

function segmentDistance(p: Point, a: Point, b: Point) {
  const dx = b.x - a.x,
    dy = b.y - a.y;
  const t =
    dx || dy
      ? clamp(((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy), 0, 1)
      : 0;
  return distance(p, { x: a.x + dx * t, y: a.y + dy * t });
}

/** Original editor's Douglas–Peucker reduction, with an explicit stack for long strokes. */
export function simplify(points: Point[], tolerance: number): Point[] {
  if (points.length < 3) return points.slice();
  const keep = new Set([0, points.length - 1]);
  const stack: [number, number][] = [[0, points.length - 1]];
  while (stack.length) {
    const [start, end] = stack.pop()!;
    let farthest = tolerance,
      split = -1;
    for (let i = start + 1; i < end; i++) {
      const d = segmentDistance(points[i]!, points[start]!, points[end]!);
      if (d > farthest) {
        farthest = d;
        split = i;
      }
    }
    if (split >= 0) {
      keep.add(split);
      stack.push([start, split], [split, end]);
    }
  }
  return [...keep].sort((a, b) => a - b).map((i) => points[i]!);
}

function distinct(points: readonly Point[], minimum: number): Point[] {
  if (!points.length) return [];
  const out = [{ ...points[0]! }];
  for (const p of points.slice(1))
    if (distance(p, out.at(-1)!) >= minimum) out.push({ ...p });
  if (distance(points.at(-1)!, out.at(-1)!) > 0.001)
    out.push({ ...points.at(-1)! });
  return out;
}

function resample(points: Point[], spacing: number): Point[] {
  if (points.length <= 2) return points.slice();
  const out = [{ ...points[0]! }];
  let start = { ...points[0]! },
    carried = 0;
  for (const target of points.slice(1)) {
    let dx = target.x - start.x,
      dy = target.y - start.y,
      length = Math.hypot(dx, dy);
    if (length <= 0.0001) continue;
    while (carried + length >= spacing) {
      const ratio = (spacing - carried) / length;
      start = { x: start.x + dx * ratio, y: start.y + dy * ratio };
      out.push({ ...start });
      dx = target.x - start.x;
      dy = target.y - start.y;
      length = Math.hypot(dx, dy);
      carried = 0;
      if (length <= 0.0001) break;
    }
    carried += length;
    start = { ...target };
  }
  const last = points.at(-1)!;
  if (distance(last, out.at(-1)!) > spacing * 0.2) out.push({ ...last });
  else out[out.length - 1] = { ...last };
  return out;
}

/** Same spacing, corner-aware smoothing and tolerance as the current original creator. */
export function preparePenPoints(
  points: readonly Point[],
  strokeWidth: number,
  zoom: number,
): Point[] {
  const pixel = 1 / Math.max(0.1, zoom);
  const minimum = clamp(0.45 + strokeWidth * 0.035, 0.45, 1.5) * pixel;
  const spacing = clamp(1.45 + strokeWidth * 0.08, 1.4, 3.4) * pixel;
  const tolerance = clamp(2 + strokeWidth * 0.1, 2, 5) * pixel;
  let current = resample(distinct(points, minimum), spacing);
  for (let pass = 0; pass < 2; pass++) {
    current = current.map((p, i) => {
      if (!i || i === current.length - 1) return p;
      const prev = current[i - 1]!,
        next = current[i + 1]!;
      const a = distance(p, prev) || 1,
        b = distance(next, p) || 1;
      const dot =
        ((p.x - prev.x) * (next.x - p.x) + (p.y - prev.y) * (next.y - p.y)) /
        (a * b);
      const weight = 0.22 * clamp((dot - 0.25) / 0.75, 0, 1);
      return {
        x: p.x * (1 - 2 * weight) + (prev.x + next.x) * weight,
        y: p.y * (1 - 2 * weight) + (prev.y + next.y) * weight,
      };
    });
  }
  return simplify(current, tolerance);
}

/** Original corner-aware tangents; a closed contour has one shared seam anchor. */
export function penAnchors(
  points: readonly Point[],
  closed = false,
  smooth = 0,
): PathAnchor[] {
  const ps = points.slice();
  if (closed && ps.length > 1 && distance(ps[0]!, ps.at(-1)!) < 0.001) ps.pop();
  return ps.map((p, i) => {
    const hasIn = closed || i > 0,
      hasOut = closed || i < ps.length - 1;
    const prev = hasIn ? ps[(i + ps.length - 1) % ps.length]! : p;
    const next = hasOut ? ps[(i + 1) % ps.length]! : p;
    const tangent = distance(next, prev) || 1,
      ux = (next.x - prev.x) / tangent,
      uy = (next.y - prev.y) / tangent;
    const a = distance(p, prev),
      b = distance(next, p);
    const dot =
      a && b
        ? ((p.x - prev.x) * (next.x - p.x) + (p.y - prev.y) * (next.y - p.y)) /
          (a * b)
        : 1;
    const factor =
      (clamp((dot + 0.15) / 1.15, 0.18, 1) * (1 - smooth) + smooth) / 3;
    return {
      ...p,
      ...(hasIn
        ? { incoming: { x: p.x - ux * a * factor, y: p.y - uy * a * factor } }
        : {}),
      ...(hasOut
        ? { outgoing: { x: p.x + ux * b * factor, y: p.y + uy * b * factor } }
        : {}),
    };
  });
}

/** A stroke must first leave the start vicinity; a click/jitter must not close itself. */
export function canClosePen(
  points: readonly Point[],
  end: Point,
  zoom: number,
  touch = false,
) {
  const radius = (touch ? 18 : 8) / zoom;
  return (
    points.length > 2 &&
    distance(points[0]!, end) <= radius &&
    points.some((p) => distance(p, points[0]!) >= radius * 3)
  );
}
